import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { GanttGroup } from './types';
import { collectEditableSeries } from '../../utils/ganttUtils';
import type { ContextMenuItem } from '../ContextMenu/ContextMenu';
import type { RowMoveDirection } from '../../services/treatmentPlanRowOrderService';
import { ContextMenu } from '../ContextMenu/ContextMenu';
import { GanttGroupLabelInput } from './GanttGroupLabelInput';
import MenuDotsIcon from '../../assets/icons/menu-dots.svg?react';
import styles from './GanttGroupLabel.module.css';

interface GanttGroupLabelProps {
  group: GanttGroup;
  isEditMode?: boolean;
  onRenameRow?: (group: GanttGroup, name: string) => void;
  onRemoveRow?: (group: GanttGroup) => void;
  // Undefined reads as "cannot move": the read-only preview passes neither, and
  // the chart passes the row's position among the groups.
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onMoveRow?: (group: GanttGroup, direction: RowMoveDirection) => void;
}

export const GanttGroupLabel = ({
  group,
  isEditMode,
  onRenameRow,
  onRemoveRow,
  canMoveUp,
  canMoveDown,
  onMoveRow
}: GanttGroupLabelProps) => {
  const { t } = useTranslation();
  const [menuAnchorRect, setMenuAnchorRect] = useState<DOMRect | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Derived rather than stored: ending edit mode releases the lock and takes the
  // toolbar away, so a rename must stop existing with it rather than live on
  // with no way to be saved or discarded. GanttChart also remounts this on the
  // same signal, which is what stops a stale flag reopening the field later.
  const isRenamingActive = isEditMode === true && isRenaming;

  // Focus would otherwise fall to the document body when the field unmounts,
  // restarting the next Tab at the top of a rail that can hold dozens of rows.
  const endRenaming = useCallback((): void => {
    setIsRenaming(false);
    triggerRef.current?.focus();
  }, []);

  // Leaving the field discards the edit, so it always resolves one way or the
  // other rather than staying open behind whatever the user did next.
  useEffect(() => {
    if (!isRenamingActive) return;
    const handlePointerDown = (e: MouseEvent): void => {
      if (!overlayRef.current?.contains(e.target as Node)) {
        endRenaming();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isRenamingActive, endRenaming]);

  // A row holding only carried-over plans has nothing this year may rename or remove.
  const hasEditablePlans = collectEditableSeries(group).length > 0;
  const menuItems: ContextMenuItem[] = [
    {
      label: t('rename-row'),
      isDisabled: !hasEditablePlans,
      onClick: () => {
        setIsRenaming(true);
      }
    },
    {
      label: t('move-row-up'),
      isDisabled: canMoveUp !== true,
      onClick: () => {
        onMoveRow?.(group, 'up');
      }
    },
    {
      label: t('move-row-down'),
      isDisabled: canMoveDown !== true,
      onClick: () => {
        onMoveRow?.(group, 'down');
      }
    },
    {
      label: t('remove-row'),
      isDisabled: !hasEditablePlans,
      onClick: () => {
        onRemoveRow?.(group);
      }
    }
  ];

  // The name and the menu stay in flow while renaming, with the field laid over
  // them. The rail column is sized by its widest label, so swapping them out for
  // a field would resize the whole column mid-edit.
  return (
    <div className={styles.label}>
      {/* Hidden from assistive tech while renaming: it is only still here to
          size the column, and the field beside it carries the real name. */}
      <span className={styles.labelText} aria-hidden={isRenamingActive}>
        {group.templateName}
      </span>
      <button
        ref={triggerRef}
        type="button"
        className={styles.menuButton}
        aria-label={t('row-actions')}
        aria-hidden={!isEditMode || isRenamingActive}
        aria-haspopup="menu"
        aria-expanded={menuAnchorRect !== null}
        tabIndex={isEditMode && !isRenamingActive ? 0 : -1}
        onClick={(e) => {
          setMenuAnchorRect(e.currentTarget.getBoundingClientRect());
        }}
      >
        <MenuDotsIcon className={styles.menuIcon} />
      </button>
      {isRenamingActive && (
        <div ref={overlayRef} className={styles.renameOverlay}>
          <GanttGroupLabelInput
            initialName={group.templateName}
            ariaLabel={t('rename-row')}
            onCommit={(name) => {
              onRenameRow?.(group, name);
              endRenaming();
            }}
            onCancel={endRenaming}
          />
        </div>
      )}
      {menuAnchorRect &&
        createPortal(
          <ContextMenu
            anchorRect={menuAnchorRect}
            items={menuItems}
            onClose={() => {
              setMenuAnchorRect(null);
            }}
          />,
          document.body
        )}
    </div>
  );
};
